import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { User, Heart, Map, Sparkles, Star, Target, BookOpen, Zap, CheckCircle } from 'lucide-react';
import personalData from '../content/personal.json';

const iconMap = {
  User: User,
  Heart: Heart,
  Map: Map,
  Sparkles: Sparkles,
  Star: Star,
  Target: Target,
  BookOpen: BookOpen,
  Zap: Zap,
  CheckCircle: CheckCircle,
};

const PersonalSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { sectionTitle, description, services: servicesData, processSteps: processStepsData, personalTouchTitle, personalTouchParagraphs, goalsTitle, goals, availabilityTitle, availabilityItems } = personalData;
  
  // CMS format: simple lists are arrays of objects
  const personalTouchParagraphsArray = personalTouchParagraphs ? personalTouchParagraphs.map(p => p.paragraph || p) : [];
  const goalsArray = goals ? goals.map(g => g.goal || g) : [];
  const availabilityItemsArray = availabilityItems ? availabilityItems.map(i => i.item || i) : [];

  const services = servicesData.map(service => ({
    ...service,
    icon: React.createElement(iconMap[service.icon] || Heart, { size: 36 })
  }));

  const processSteps = processStepsData.map(step => ({
    ...step,
    icon: React.createElement(iconMap[step.icon] || Target, { size: 28 })
  }));

  return (
    <section id="personal" className="section-padding bg-gradient-to-b from-nitzan-light-bg to-white">
      <div className="container-width">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-gradient-to-r from-nitzan-mint/20 to-nitzan-teal/20 px-6 py-3 rounded-full mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <User className="text-nitzan-teal" size={20} />
            <span className="text-nitzan-teal font-medium">ליווי אישי</span>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">{sectionTitle}</span>
          </h2>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="space-y-8 mb-16">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + index * 0.15, duration: 0.6 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden border border-nitzan-sage/20 hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="grid lg:grid-cols-3 gap-8">
                <div className={`lg:col-span-1 bg-gradient-to-br ${service.gradient} p-8 text-white flex flex-col justify-center`}>
                  <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-3xl font-bold mb-3">{service.title}</h3>
                  <p className="text-white/90 text-lg">{service.description}</p>
                </div>

                <div className="lg:col-span-2 p-8">
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.options.map((option, idx) => (
                      <div key={idx} className="bg-nitzan-light-bg rounded-2xl p-5 hover:shadow-md transition-shadow">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="text-nitzan-mint flex-shrink-0 mt-1" size={20} />
                          <div>
                            <h4 className="font-bold text-nitzan-teal mb-1">{option.name}</h4>
                            <p className="text-gray-600 text-sm">{option.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Process Steps */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mb-16"
        >
          <h3 className="text-3xl font-bold text-nitzan-teal mb-8 text-center">איך עובד התהליך?</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.8 + index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-nitzan-sage/20 text-center hover:shadow-xl transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-nitzan-mint to-nitzan-teal rounded-full flex items-center justify-center text-white mx-auto mb-4">
                  {step.icon}
                </div>
                <h4 className="text-lg font-bold text-nitzan-teal mb-2">{step.title}</h4>
                <p className="text-gray-700 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Personal Touch */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="bg-gradient-to-br from-nitzan-mint/10 via-nitzan-teal/10 to-nitzan-sage/10 rounded-3xl p-8 md:p-12 mb-16"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <Heart className="text-nitzan-terracotta mb-6" size={48} />
              <h3 className="text-3xl font-bold text-nitzan-teal mb-4">{personalTouchTitle}</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                {personalTouchParagraphsArray.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h4 className="text-xl font-bold text-nitzan-teal mb-3">{goalsTitle}</h4>
                <ul className="space-y-3 text-gray-700">
                  {goalsArray.map((goal, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Star className="text-nitzan-mint flex-shrink-0 mt-1" size={18} />
                      <span>{goal}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-r from-nitzan-teal to-nitzan-mint text-white rounded-2xl p-6 shadow-lg">
                <h4 className="text-xl font-bold mb-3">{availabilityTitle}</h4>
                {availabilityItemsArray.map((item, index) => (
                  <p key={index} className={index < availabilityItemsArray.length - 1 ? "mb-2" : ""}>✨ {item}</p>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.0, duration: 0.8 }}
          className="text-center"
        >
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border-2 border-nitzan-mint">
            <Sparkles className="text-nitzan-mint mx-auto mb-6" size={48} />
            <h3 className="text-3xl font-bold text-nitzan-teal mb-4">מוכנים להתחיל?</h3>
            <p className="text-xl text-gray-700 mb-6 max-w-2xl mx-auto">
              בואו נקבע שיחת היכרות בלי התחייבות, ונראה איך אני יכולה ללוות אותך במסע שלך
            </p>
            <Link to="/contact">
              <motion.div
                className="inline-block bg-gradient-to-r from-nitzan-teal to-nitzan-mint text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                בואי ניצור קשר
              </motion.div>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PersonalSection;

